import { Injectable, inject, signal } from '@angular/core';
import {
  Location,
  DonationCategory,
  DonationDepartment,
  AnalyticsSummary,
  DonationSession,
  DonorTier,
  DonationStatus,
  ContainerStatus,
  ContainerDest,
  ContainerType,
  PresortMethod,
  PresortStatus,
  LocationStatus,
  DonationRole,
  DonationMethod,
  DonationScope,
  PointsCalcMethod,
  RewardType,
  RedemptionStatus,
  Campaign,
  CampaignStatus,
  CampaignNotification,
  NotificationChannel,
  NotificationTemplate,
  DonorTierLabel,
  ScheduledDonation
} from '../models/domain.models';
import { ApiStatus } from '../models/api-contracts';
import { ApiDonorResponse } from '../../modules/donation-portal/features/donors/models/donor-management.response';
import {
  DonationResponse,
  DonationItemResponse
} from '../../modules/donation-portal/features/donations/models/donations.response';
import { ApiContainerResponse } from '../../modules/donation-portal/features/containers/models/containers.response';
import {
  ApiAppConfigResponse,
  ApiRewardDefinitionResponse,
  ApiRewardTransactionResponse,
  LoyaltyTierConfigResponse
} from '../../modules/donation-portal/features/settings/models/settings.response';
import { ApiPresortQueueResponse } from '../../modules/donation-portal/features/presort/models/presort.response';
import { StorageService } from './storage.service';

@Injectable({ providedIn: 'root' })
export class MockDataService {
  private storage = inject(StorageService);

  readonly session: DonationSession = {
    staffId: 1,
    staffName: 'Alex Morgan',
    role: DonationRole.Attendant,
    locationId: 1,
    locationName: 'Downtown Store'
  };

  // ── Phase 1: System config (Req 2, 3, 4) ──────────────────────────────────
  readonly appConfig = signal<ApiAppConfigResponse>({
    storeId: 1,
    companyId: 1,
    isCashAccepted: true,
    associationWindowHours: 24,
    pointsPerItem: 10,
    pointsPerDollar: 20,
    pointsCalcMethod: PointsCalcMethod.PerItem,
    requireApproval: false,
    emailForReceipt: false,
    emailForLogin: false,
    emailForCampaigns: true
  });

  updateAppConfig(patch: Partial<ApiAppConfigResponse>): void {
    this.appConfig.update(c => ({ ...c, ...patch }));
  }

  // ── Phase 1: Reward definitions seed data (Req 5) ─────────────────────────
  private _rewardDefs = signal<ApiRewardDefinitionResponse[]>([
    {
      id: 1,
      companyId: 1,
      referenceNumber: 'RD-001',
      name: '$5 Discount',
      description: 'Redeemable at POS',
      pointsRequired: 100,
      rewardType: RewardType.Discount,
      value: 5,
      status: ApiStatus.Active,
      isGiftable: true,
      totalRedemptions: 12
    },
    {
      id: 2,
      companyId: 1,
      referenceNumber: 'RD-002',
      name: '$10 Discount',
      description: 'Redeemable at POS',
      pointsRequired: 200,
      rewardType: RewardType.Discount,
      value: 10,
      status: ApiStatus.Active,
      isGiftable: true,
      totalRedemptions: 8
    },
    {
      id: 3,
      companyId: 1,
      referenceNumber: 'RD-003',
      name: '$25 Voucher',
      description: 'Store credit voucher',
      pointsRequired: 450,
      rewardType: RewardType.Voucher,
      value: 25,
      status: ApiStatus.Active,
      isGiftable: true,
      totalRedemptions: 3,
      totalRedemptionLimit: 50
    },
    {
      id: 4,
      companyId: 1,
      referenceNumber: 'RD-004',
      name: '$10 Cashback',
      description: 'Direct cashback',
      pointsRequired: 250,
      rewardType: RewardType.Cashback,
      value: 10,
      status: ApiStatus.Active,
      isGiftable: false,
      totalRedemptions: 5,
      maxRedemptionsPerUser: 3
    },
    {
      id: 5,
      companyId: 1,
      referenceNumber: 'RD-005',
      name: 'Gift Basket',
      description: 'Curated gift basket',
      pointsRequired: 900,
      rewardType: RewardType.Gift,
      value: 50,
      status: ApiStatus.Active,
      isGiftable: true,
      totalRedemptions: 1
    }
  ]);
  readonly rewardDefinitions = this._rewardDefs.asReadonly();

  // ── Phase 1: Reward transactions seed data (Req 5, 7) ─────────────────────
  private _rewardTxns = signal<ApiRewardTransactionResponse[]>([
    {
      id: 1,
      companyId: 1,
      referenceNumber: 'RTX-001',
      donorId: 1,
      rewardId: 1,
      pointsUsed: 100,
      status: RedemptionStatus.Fulfilled,
      isGift: false,
      approvedAt: '2026-03-10T00:00:00',
      fulfilledAt: '2026-03-10T00:00:00'
    },
    {
      id: 2,
      companyId: 1,
      referenceNumber: 'RTX-002',
      donorId: 2,
      rewardId: 3,
      pointsUsed: 450,
      status: RedemptionStatus.Approved,
      isGift: false,
      voucherCode: 'VCH-2026-A1B2',
      approvedAt: '2026-03-12T00:00:00'
    },
    {
      id: 3,
      companyId: 1,
      referenceNumber: 'RTX-003',
      donorId: 1,
      rewardId: 2,
      pointsUsed: 200,
      status: RedemptionStatus.Pending,
      isGift: false
    }
  ]);
  readonly rewardTransactions = this._rewardTxns.asReadonly();

  // ── Phase 1: Campaign seed data (Req 6) ───────────────────────────────────
  private _campaigns = signal<Campaign[]>([
    {
      id: 1,
      referenceNumber: 'CMP-001',
      name: 'Winter Jacket Drive',
      description: 'Reach donors who have given winter clothing in the past',
      startDate: new Date('2026-04-01'),
      endDate: new Date('2026-04-30'),
      status: CampaignStatus.Draft,
      channel: NotificationChannel.Email,
      targetCriteria: [
        { categoryKey: 'clothing', categoryName: 'Clothing', subCategoryKey: 'womens', subCategoryName: "Women's" }
      ],
      notificationHistory: [],
      emailTemplate: {
        channel: NotificationChannel.Email,
        subject: 'Help Us Stock Up on Winter Jackets, {{first_name}}!',
        body: `<p>Hi <strong>{{donor_name}}</strong>,</p>\n<p>As a valued <strong>{{tier}}</strong> member of {{org_name}}, we're reaching out to let you know our Winter Jacket Drive is starting soon.</p>\n<p>Your previous clothing donations have made a real difference. We'd love your help again this year — every jacket counts!</p>\n<p>You currently have <strong>{{points}} points</strong> in your loyalty account. Donate during this campaign to earn bonus points.</p>\n<p>Thank you for your generosity,<br>The {{org_name}} Team</p>`,
        blocks: [
          { id: 's1', type: 'header' as const, content: 'Winter Jacket Drive', align: 'center' as const, level: 1 },
          {
            id: 's2',
            type: 'text' as const,
            content:
              "Hi {{donor_name}},\n\nAs a valued {{tier}} member of {{org_name}}, we're reaching out to let you know our Winter Jacket Drive is starting soon.",
            align: 'left' as const
          },
          {
            id: 's3',
            type: 'text' as const,
            content:
              "Your previous clothing donations have made a real difference. We'd love your help again this year — every jacket counts!",
            align: 'left' as const
          },
          {
            id: 's4',
            type: 'text' as const,
            content:
              'You currently have {{points}} points in your loyalty account. Donate during this campaign to earn bonus points.',
            align: 'left' as const
          },
          {
            id: 's5',
            type: 'button' as const,
            content: 'Learn More',
            align: 'center' as const,
            meta: 'https://example.org/jackets'
          },
          { id: 's6', type: 'divider' as const, content: '', align: 'center' as const },
          {
            id: 's7',
            type: 'text' as const,
            content: 'Thank you for your generosity,\nThe {{org_name}} Team',
            align: 'left' as const
          }
        ],
        updatedAt: new Date('2026-03-18')
      },
      createdAt: new Date('2026-03-18'),
      createdByStaffId: 1
    },
    {
      id: 2,
      referenceNumber: 'CMP-002',
      name: 'Electronics Recycling Push',
      description: 'Encourage electronics donors to donate again',
      startDate: new Date('2026-03-20'),
      endDate: new Date('2026-03-31'),
      status: CampaignStatus.Active,
      channel: NotificationChannel.Both,
      targetCriteria: [{ categoryKey: 'electronics', categoryName: 'Electronics' }],
      notificationHistory: [
        {
          donorId: 5,
          donorName: 'David Chen',
          channel: NotificationChannel.Email,
          sentAt: new Date('2026-03-20'),
          success: true
        },
        {
          donorId: 10,
          donorName: 'Lisa Anderson',
          channel: NotificationChannel.SMS,
          sentAt: new Date('2026-03-20'),
          success: true
        }
      ],
      emailTemplate: {
        channel: NotificationChannel.Email,
        subject: "Got Old Electronics? We'll Take Them, {{first_name}}!",
        body: `<p>Hi <strong>{{donor_name}}</strong>,</p>\n<p>{{org_name}} is running an Electronics Recycling Push and we thought of you!</p>\n<p>Whether it's old phones, laptops, or cables — we'll make sure they're recycled responsibly.</p>\n<p>Drop off anytime before March 31. As a <strong>{{tier}}</strong> donor with <strong>{{points}} points</strong>, you're making a big impact.</p>\n<p>See you soon!<br>{{org_name}}</p>`,
        blocks: [
          {
            id: 'e1',
            type: 'header' as const,
            content: 'Electronics Recycling Push',
            align: 'center' as const,
            level: 1
          },
          {
            id: 'e2',
            type: 'text' as const,
            content:
              'Hi {{donor_name}},\n\n{{org_name}} is running an Electronics Recycling Push and we thought of you!',
            align: 'left' as const
          },
          {
            id: 'e3',
            type: 'text' as const,
            content: "Whether it's old phones, laptops, or cables — we'll make sure they're recycled responsibly.",
            align: 'left' as const
          },
          {
            id: 'e4',
            type: 'text' as const,
            content:
              "Drop off anytime before March 31. As a {{tier}} donor with {{points}} points, you're making a big impact.",
            align: 'left' as const
          },
          {
            id: 'e5',
            type: 'button' as const,
            content: 'Find a Drop-off',
            align: 'center' as const,
            meta: 'https://example.org/recycle'
          },
          { id: 'e6', type: 'text' as const, content: 'See you soon!\n{{org_name}}', align: 'left' as const }
        ],
        updatedAt: new Date('2026-03-15')
      },
      smsTemplate: {
        channel: NotificationChannel.SMS,
        subject: '',
        body: `Hi {{first_name}}! {{org_name}} is collecting electronics thru Mar 31. Drop off old devices & earn bonus points! You have {{points}} pts as a {{tier}} member. Details: example.org/recycle`,
        updatedAt: new Date('2026-03-15')
      },
      createdAt: new Date('2026-03-15'),
      createdByStaffId: 1
    }
  ]);
  readonly campaigns = this._campaigns.asReadonly();

  readonly loyaltyTiers: LoyaltyTierConfigResponse[] = [
    {
      companyId: 1,
      tier: DonorTier.Bronze,
      label: 'Bronze',
      minDonations: 1,
      pointsMultiplier: 1,
      perks: 'Digital receipts|Donation history|Basic rewards'
    },
    {
      companyId: 1,
      tier: DonorTier.Silver,
      label: 'Silver',
      minDonations: 5,
      pointsMultiplier: 1.5,
      perks: 'All Bronze perks|1.5× points|Priority processing|Monthly impact report'
    },
    {
      companyId: 1,
      tier: DonorTier.Gold,
      label: 'Gold',
      minDonations: 10,
      pointsMultiplier: 2,
      perks: 'All Silver perks|2× points|VIP events|Year-end recognition'
    },
    {
      companyId: 1,
      tier: DonorTier.Platinum,
      label: 'Platinum',
      minDonations: 25,
      pointsMultiplier: 3,
      perks: 'All Gold perks|3× points|Dedicated contact|Advisory board invitation'
    }
  ];

  readonly categories: DonationCategory[] = [
    {
      key: 'clothing',
      name: 'Clothing',
      icon: '👔',
      color: '#0066ff',
      estimatedValue: 12,
      active: true,
      sortOrder: 1,
      subCategories: [
        { key: 'womens', name: "Women's", icon: '👗', estimatedValue: 12 },
        { key: 'mens', name: "Men's", icon: '👔', estimatedValue: 12 },
        { key: 'childrens', name: "Children's", icon: '🧒', estimatedValue: 8 }
      ]
    },
    { key: 'shoes', name: 'Shoes', icon: '👟', color: '#10b981', estimatedValue: 15, active: true, sortOrder: 2 },
    {
      key: 'books',
      name: 'Books & Media',
      icon: '📚',
      color: '#f59e0b',
      estimatedValue: 5,
      active: true,
      sortOrder: 3,
      subCategories: [
        { key: 'books', name: 'Books', icon: '📖', estimatedValue: 5 },
        { key: 'dvds', name: 'DVDs/CDs', icon: '💿', estimatedValue: 3 },
        { key: 'games', name: 'Video Games', icon: '🎮', estimatedValue: 15 }
      ]
    },
    {
      key: 'electronics',
      name: 'Electronics',
      icon: '📱',
      color: '#8b5cf6',
      estimatedValue: 50,
      active: true,
      sortOrder: 4,
      subCategories: [
        { key: 'phones', name: 'Phones/Tablets', icon: '📱', estimatedValue: 80 },
        { key: 'appliances', name: 'Appliances', icon: '🔌', estimatedValue: 25 },
        { key: 'cables', name: 'Cables', icon: '🔋', estimatedValue: 5 }
      ]
    },
    {
      key: 'furniture',
      name: 'Furniture',
      icon: '🪑',
      color: '#ef4444',
      estimatedValue: 125,
      active: true,
      sortOrder: 5
    },
    {
      key: 'housewares',
      name: 'Housewares',
      icon: '🍳',
      color: '#06b6d4',
      estimatedValue: 8,
      active: true,
      sortOrder: 6,
      subCategories: [
        { key: 'kitchen', name: 'Kitchen', icon: '🍳', estimatedValue: 8 },
        { key: 'glassware', name: 'Glassware', icon: '🥂', estimatedValue: 6 }
      ]
    },
    {
      key: 'home_decor',
      name: 'Home Décor',
      icon: '🖼️',
      color: '#ec4899',
      estimatedValue: 15,
      active: true,
      sortOrder: 7
    },
    { key: 'toys', name: 'Toys & Games', icon: '🧸', color: '#f97316', estimatedValue: 10, active: true, sortOrder: 8 },
    {
      key: 'linens',
      name: 'Linens & Textiles',
      icon: '🛏️',
      color: '#14b8a6',
      estimatedValue: 7,
      active: true,
      sortOrder: 9
    },
    {
      key: 'accessories',
      name: 'Accessories/Jewelry',
      icon: '👜',
      color: '#a855f7',
      estimatedValue: 20,
      active: true,
      sortOrder: 10
    },
    {
      key: 'sporting',
      name: 'Sporting Goods',
      icon: '⚽',
      color: '#22c55e',
      estimatedValue: 25,
      active: true,
      sortOrder: 11
    },
    {
      key: 'seasonal',
      name: 'Seasonal/Holiday',
      icon: '🎄',
      color: '#dc2626',
      estimatedValue: 12,
      active: true,
      sortOrder: 12
    },
    {
      key: 'salvage',
      name: 'Salvage/Recycling',
      icon: 'refresh',
      color: '#6b7280',
      estimatedValue: 0,
      active: true,
      sortOrder: 13
    }
  ];

  readonly departments: DonationDepartment[] = [
    {
      key: 'clothes',
      name: 'Clothing & Accessories',
      icon: '👔',
      color: '#0066ff',
      estimatedValue: 12,
      active: true,
      sortOrder: 1,
      categories: [
        {
          key: 'clothing',
          name: 'Clothing',
          estimatedValue: 12,
          subCategories: [
            { key: 'womens', name: "Women's", icon: '👗', estimatedValue: 12 },
            { key: 'mens', name: "Men's", icon: '👔', estimatedValue: 12 },
            { key: 'childrens', name: "Children's", icon: '🧒', estimatedValue: 8 }
          ]
        },
        { key: 'shoes', name: 'Shoes', estimatedValue: 15 },
        { key: 'accessories', name: 'Accessories/Jewelry', estimatedValue: 20 }
      ]
    },
    {
      key: 'media',
      name: 'Electronics & Media',
      icon: '📱',
      color: '#8b5cf6',
      estimatedValue: 20,
      active: true,
      sortOrder: 2,
      categories: [
        {
          key: 'electronics',
          name: 'Electronics',
          estimatedValue: 50,
          subCategories: [
            { key: 'phones', name: 'Phones/Tablets', icon: '📱', estimatedValue: 80 },
            { key: 'appliances', name: 'Appliances', icon: '🔌', estimatedValue: 25 },
            { key: 'cables', name: 'Cables', icon: '🔋', estimatedValue: 5 }
          ]
        },
        {
          key: 'books',
          name: 'Books & Media',
          estimatedValue: 5,
          subCategories: [
            { key: 'books', name: 'Books', icon: '📖', estimatedValue: 5 },
            { key: 'dvds', name: 'DVDs/CDs', icon: '💿', estimatedValue: 3 },
            { key: 'games', name: 'Video Games', icon: '🎮', estimatedValue: 15 }
          ]
        }
      ]
    },
    {
      key: 'home',
      name: 'Home & Living',
      icon: '🏠',
      color: '#06b6d4',
      estimatedValue: 15,
      active: true,
      sortOrder: 3,
      categories: [
        { key: 'furniture', name: 'Furniture', estimatedValue: 125 },
        {
          key: 'housewares',
          name: 'Housewares',
          estimatedValue: 8,
          subCategories: [
            { key: 'kitchen', name: 'Kitchen', icon: '🍳', estimatedValue: 8 },
            { key: 'glassware', name: 'Glassware', icon: '🥂', estimatedValue: 6 }
          ]
        },
        { key: 'home_decor', name: 'Home Décor', estimatedValue: 15 },
        { key: 'linens', name: 'Linens & Textiles', estimatedValue: 7 }
      ]
    },
    {
      key: 'recreation',
      name: 'Toys & Recreation',
      icon: '🧸',
      color: '#f97316',
      estimatedValue: 10,
      active: true,
      sortOrder: 4,
      categories: [
        { key: 'toys', name: 'Toys & Games', estimatedValue: 10 },
        { key: 'sporting', name: 'Sporting Goods', estimatedValue: 25 },
        { key: 'seasonal', name: 'Seasonal/Holiday', estimatedValue: 12 }
      ]
    },
    {
      key: 'salvage',
      name: 'Salvage & Recycling',
      icon: '♻️',
      color: '#6b7280',
      estimatedValue: 0,
      active: true,
      sortOrder: 5,
      categories: [{ key: 'salvage', name: 'Salvage/Recycling', estimatedValue: 0 }]
    }
  ];

  readonly locations: Location[] = [
    {
      id: 1,
      referenceNumber: 'LOC-001',
      name: 'Downtown Store',
      address: '123 Main Street',
      phone: '(555)100-2000',
      managerName: 'Jane Smith',
      staffCount: 5,
      status: LocationStatus.Open,
      hours: 'Mon–Sat 9AM–6PM',
      donationsToday: 15,
      itemsToday: 142,
      revenueToday: 2840
    },
    {
      id: 2,
      referenceNumber: 'LOC-002',
      name: 'Northside Store',
      address: '456 Oak Avenue',
      phone: '(555)200-3000',
      managerName: 'John Doe',
      staffCount: 4,
      status: LocationStatus.Open,
      hours: 'Mon–Sat 9AM–6PM',
      donationsToday: 12,
      itemsToday: 98,
      revenueToday: 1960
    },
    {
      id: 3,
      referenceNumber: 'LOC-003',
      name: 'Westside Store',
      address: '789 Pine Road',
      phone: '(555)300-4000',
      managerName: 'Anna Roberts',
      staffCount: 3,
      status: LocationStatus.Busy,
      hours: 'Mon–Fri 10AM–5PM',
      donationsToday: 8,
      itemsToday: 64,
      revenueToday: 1280
    },
    {
      id: 4,
      referenceNumber: 'LOC-004',
      name: 'Eastside Store',
      address: '321 Elm Boulevard',
      phone: '(555)400-5000',
      managerName: 'Mark Lee',
      staffCount: 2,
      status: LocationStatus.Closed,
      hours: 'Mon–Fri 10AM–5PM (Reduced)',
      donationsToday: 2,
      itemsToday: 18,
      revenueToday: 360
    }
  ];

  readonly donors: ApiDonorResponse[] = [
    {
      id: 1,
      companyId: 1,
      referenceNumber: 'D-001',
      userId: null,
      firstName: 'Sarah',
      lastName: 'Johnson',
      email: 'sarah.johnson@email.com',
      phone: '(555) 201-1001',
      loyaltyTier: DonorTier.Gold,
      loyaltyPoints: 1240,
      totalDonations: 42,
      lifetimeValue: 8240,
      preferredStoreId: 1,
      joinDate: '2022-03-15',
      lastDonationDate: '2026-03-01',
      status: ApiStatus.Active
    },
    {
      id: 2,
      companyId: 1,
      referenceNumber: 'D-002',
      userId: null,
      firstName: 'Michael',
      lastName: 'Chen',
      email: 'michael.chen@email.com',
      phone: '(555) 202-1002',
      loyaltyTier: DonorTier.Gold,
      loyaltyPoints: 890,
      totalDonations: 28,
      lifetimeValue: 5640,
      preferredStoreId: 1,
      joinDate: '2022-08-20',
      lastDonationDate: '2026-02-28',
      status: ApiStatus.Active
    },
    {
      id: 3,
      companyId: 1,
      referenceNumber: 'D-003',
      userId: null,
      firstName: 'Emily',
      lastName: 'Patel',
      email: 'emily.patel@email.com',
      phone: '(555) 203-1003',
      loyaltyTier: DonorTier.Silver,
      loyaltyPoints: 450,
      totalDonations: 15,
      lifetimeValue: 3120,
      preferredStoreId: 1,
      joinDate: '2023-01-10',
      lastDonationDate: '2026-03-02',
      status: ApiStatus.Active
    },
    {
      id: 4,
      companyId: 1,
      referenceNumber: 'D-004',
      userId: null,
      firstName: 'James',
      lastName: 'Williams',
      email: 'james.williams@email.com',
      phone: '(555) 204-1004',
      loyaltyTier: DonorTier.Bronze,
      loyaltyPoints: 120,
      totalDonations: 3,
      lifetimeValue: 480,
      preferredStoreId: 1,
      joinDate: '2025-11-05',
      lastDonationDate: '2026-02-10',
      status: ApiStatus.Active
    },
    {
      id: 5,
      companyId: 1,
      referenceNumber: 'D-005',
      userId: null,
      firstName: 'David',
      lastName: 'Kim',
      email: 'david.kim@email.com',
      phone: '(555) 205-1005',
      loyaltyTier: DonorTier.Platinum,
      loyaltyPoints: 4200,
      totalDonations: 67,
      lifetimeValue: 15800,
      preferredStoreId: 2,
      joinDate: '2021-06-01',
      lastDonationDate: '2026-03-10',
      status: ApiStatus.Active
    },
    {
      id: 6,
      companyId: 1,
      referenceNumber: 'D-006',
      userId: null,
      firstName: 'Rachel',
      lastName: 'Torres',
      email: 'rachel.torres@email.com',
      phone: '(555) 206-1006',
      loyaltyTier: DonorTier.Gold,
      loyaltyPoints: 1080,
      totalDonations: 38,
      lifetimeValue: 7240,
      preferredStoreId: 1,
      joinDate: '2022-05-12',
      lastDonationDate: '2026-03-05',
      status: ApiStatus.Active
    },
    {
      id: 7,
      companyId: 1,
      referenceNumber: 'D-007',
      userId: null,
      firstName: 'Kevin',
      lastName: 'Brown',
      email: 'kevin.brown@email.com',
      phone: '(555) 207-1007',
      loyaltyTier: DonorTier.Silver,
      loyaltyPoints: 520,
      totalDonations: 17,
      lifetimeValue: 3420,
      preferredStoreId: 1,
      joinDate: '2023-04-08',
      lastDonationDate: '2026-03-08',
      status: ApiStatus.Active
    },
    {
      id: 8,
      companyId: 1,
      referenceNumber: 'D-008',
      userId: null,
      firstName: 'Megan',
      lastName: 'Garcia',
      email: 'megan.garcia@email.com',
      phone: '(555) 208-1008',
      loyaltyTier: DonorTier.Bronze,
      loyaltyPoints: 95,
      totalDonations: 2,
      lifetimeValue: 380,
      preferredStoreId: 1,
      joinDate: '2026-01-15',
      lastDonationDate: '2026-02-20',
      status: ApiStatus.Active
    },
    {
      id: 9,
      companyId: 1,
      referenceNumber: 'D-009',
      userId: null,
      firstName: 'Robert',
      lastName: 'Martinez',
      email: 'robert.martinez@email.com',
      phone: '(555) 209-1009',
      loyaltyTier: DonorTier.Platinum,
      loyaltyPoints: 3850,
      totalDonations: 58,
      lifetimeValue: 14200,
      preferredStoreId: 1,
      joinDate: '2021-09-20',
      lastDonationDate: '2026-03-12',
      status: ApiStatus.Active
    },
    {
      id: 10,
      companyId: 1,
      referenceNumber: 'D-010',
      userId: null,
      firstName: 'Lisa',
      lastName: 'Anderson',
      email: 'lisa.anderson@email.com',
      phone: '(555) 210-1010',
      loyaltyTier: DonorTier.Silver,
      loyaltyPoints: 680,
      totalDonations: 22,
      lifetimeValue: 4420,
      preferredStoreId: 2,
      joinDate: '2023-02-14',
      lastDonationDate: '2026-03-04',
      status: ApiStatus.Active
    },
    {
      id: 11,
      companyId: 1,
      referenceNumber: 'D-011',
      userId: null,
      firstName: 'Daniel',
      lastName: 'Thompson',
      email: 'daniel.thompson@email.com',
      phone: '(555) 211-1011',
      loyaltyTier: DonorTier.Gold,
      loyaltyPoints: 1150,
      totalDonations: 40,
      lifetimeValue: 7820,
      preferredStoreId: 1,
      joinDate: '2022-07-03',
      lastDonationDate: '2026-02-25',
      status: ApiStatus.Active
    },
    {
      id: 12,
      companyId: 1,
      referenceNumber: 'D-012',
      userId: null,
      firstName: 'Ashley',
      lastName: 'Lee',
      email: 'ashley.lee@email.com',
      phone: '(555) 212-1012',
      loyaltyTier: DonorTier.Bronze,
      loyaltyPoints: 145,
      totalDonations: 4,
      lifetimeValue: 620,
      preferredStoreId: 1,
      joinDate: '2025-10-22',
      lastDonationDate: '2026-03-11',
      status: ApiStatus.Active
    },
    {
      id: 13,
      companyId: 1,
      referenceNumber: 'D-013',
      userId: null,
      firstName: 'Christopher',
      lastName: 'Wilson',
      email: 'chris.wilson@email.com',
      phone: '(555) 213-1013',
      loyaltyTier: DonorTier.Silver,
      loyaltyPoints: 490,
      totalDonations: 16,
      lifetimeValue: 3220,
      preferredStoreId: 1,
      joinDate: '2023-06-18',
      lastDonationDate: '2026-03-09',
      status: ApiStatus.Active
    },
    {
      id: 14,
      companyId: 1,
      referenceNumber: 'D-014',
      userId: null,
      firstName: 'Patricia',
      lastName: 'Davis',
      email: 'patricia.davis@email.com',
      phone: '(555) 214-1014',
      loyaltyTier: DonorTier.Platinum,
      loyaltyPoints: 4650,
      totalDonations: 72,
      lifetimeValue: 16840,
      preferredStoreId: 1,
      joinDate: '2021-04-10',
      lastDonationDate: '2026-03-13',
      status: ApiStatus.Active
    },
    {
      id: 15,
      companyId: 1,
      referenceNumber: 'D-015',
      userId: null,
      firstName: 'Thomas',
      lastName: 'White',
      email: 'thomas.white@email.com',
      phone: '(555) 215-1015',
      loyaltyTier: DonorTier.Gold,
      loyaltyPoints: 980,
      totalDonations: 32,
      lifetimeValue: 6240,
      preferredStoreId: 1,
      joinDate: '2022-11-05',
      lastDonationDate: '2026-03-07',
      status: ApiStatus.Active
    }
  ];

  readonly donations: DonationResponse[] = [
    {
      id: 1,
      companyId: 1,
      referenceNumber: 'DON-001',
      donorId: 1,
      isAssociatedLater: false,
      storeId: 1,
      attendantId: 1,
      donationAt: '2026-03-15T14:45:00',
      status: DonationStatus.Completed,
      method: DonationMethod.WalkIn,
      scope: DonationScope.Items,
      totalItems: 15,
      totalEstimatedValue: 137,
      loyaltyPointsEarned: 27,
      presortProcessCompleted: true,
      wasPreSortedAtDropOff: false,
      items: [
        {
          id: 1,
          donationId: 1,
          referenceNumber: 'ITEM-001',
          scope: DonationScope.Items,
          categoryKey: 'clothing',
          quantity: 8,
          estimatedValuePerItem: 12,
          totalEstimatedValue: 96
        },
        {
          id: 2,
          donationId: 1,
          referenceNumber: 'ITEM-002',
          scope: DonationScope.Items,
          categoryKey: 'books',
          quantity: 5,
          estimatedValuePerItem: 5,
          totalEstimatedValue: 25
        },
        {
          id: 3,
          donationId: 1,
          referenceNumber: 'ITEM-003',
          scope: DonationScope.Items,
          categoryKey: 'housewares',
          quantity: 2,
          estimatedValuePerItem: 8,
          totalEstimatedValue: 16
        }
      ]
    },
    {
      id: 2,
      companyId: 1,
      referenceNumber: 'DON-002',
      donorId: 2,
      isAssociatedLater: false,
      storeId: 2,
      attendantId: 2,
      donationAt: '2026-03-15T14:30:00',
      status: DonationStatus.Completed,
      method: DonationMethod.WalkIn,
      scope: DonationScope.Items,
      totalItems: 8,
      totalEstimatedValue: 108,
      loyaltyPointsEarned: 22,
      presortProcessCompleted: true,
      wasPreSortedAtDropOff: false,
      items: [
        {
          id: 4,
          donationId: 2,
          referenceNumber: 'ITEM-004',
          scope: DonationScope.Items,
          categoryKey: 'shoes',
          quantity: 4,
          estimatedValuePerItem: 15,
          totalEstimatedValue: 60
        },
        {
          id: 5,
          donationId: 2,
          referenceNumber: 'ITEM-005',
          scope: DonationScope.Items,
          categoryKey: 'clothing',
          quantity: 4,
          estimatedValuePerItem: 12,
          totalEstimatedValue: 48
        }
      ]
    },
    {
      id: 3,
      companyId: 1,
      referenceNumber: 'DON-003',
      donorId: 3,
      isAssociatedLater: false,
      storeId: 1,
      attendantId: 1,
      donationAt: '2026-03-15T14:15:00',
      status: DonationStatus.CheckedIn,
      method: DonationMethod.WalkIn,
      scope: DonationScope.Items,
      totalItems: 10,
      totalEstimatedValue: 370,
      loyaltyPointsEarned: 74,
      presortProcessCompleted: false,
      wasPreSortedAtDropOff: false,
      items: [
        {
          id: 6,
          donationId: 3,
          referenceNumber: 'ITEM-006',
          scope: DonationScope.Items,
          categoryKey: 'furniture',
          quantity: 2,
          estimatedValuePerItem: 125,
          totalEstimatedValue: 250
        },
        {
          id: 7,
          donationId: 3,
          referenceNumber: 'ITEM-007',
          scope: DonationScope.Items,
          categoryKey: 'home_decor',
          quantity: 8,
          estimatedValuePerItem: 15,
          totalEstimatedValue: 120
        }
      ]
    },
    {
      id: 4,
      companyId: 1,
      referenceNumber: 'DON-004',
      donorId: 4,
      isAssociatedLater: false,
      storeId: 1,
      attendantId: 1,
      donationAt: '2026-03-14T10:20:00',
      status: DonationStatus.Completed,
      method: DonationMethod.WalkIn,
      scope: DonationScope.Items,
      totalItems: 12,
      totalEstimatedValue: 60,
      loyaltyPointsEarned: 12,
      presortProcessCompleted: true,
      wasPreSortedAtDropOff: false,
      items: [
        {
          id: 8,
          donationId: 4,
          referenceNumber: 'ITEM-008',
          scope: DonationScope.Items,
          categoryKey: 'books',
          quantity: 12,
          estimatedValuePerItem: 5,
          totalEstimatedValue: 60
        }
      ]
    },
    {
      id: 5,
      companyId: 1,
      referenceNumber: 'DON-005',
      donorId: 5,
      isAssociatedLater: false,
      storeId: 2,
      attendantId: 2,
      donationAt: '2026-03-13T15:50:00',
      status: DonationStatus.Completed,
      method: DonationMethod.WalkIn,
      scope: DonationScope.Items,
      totalItems: 13,
      totalEstimatedValue: 270,
      loyaltyPointsEarned: 81,
      presortProcessCompleted: true,
      wasPreSortedAtDropOff: false,
      items: [
        {
          id: 9,
          donationId: 5,
          referenceNumber: 'ITEM-009',
          scope: DonationScope.Items,
          categoryKey: 'electronics',
          quantity: 3,
          estimatedValuePerItem: 50,
          totalEstimatedValue: 150
        },
        {
          id: 10,
          donationId: 5,
          referenceNumber: 'ITEM-010',
          scope: DonationScope.Items,
          categoryKey: 'clothing',
          quantity: 10,
          estimatedValuePerItem: 12,
          totalEstimatedValue: 120
        }
      ]
    },
    {
      id: 6,
      companyId: 1,
      referenceNumber: 'DON-006',
      donorId: 6,
      isAssociatedLater: false,
      storeId: 1,
      attendantId: 1,
      donationAt: '2026-03-12T11:30:00',
      status: DonationStatus.Completed,
      method: DonationMethod.WalkIn,
      scope: DonationScope.Items,
      totalItems: 17,
      totalEstimatedValue: 183,
      loyaltyPointsEarned: 37,
      presortProcessCompleted: true,
      wasPreSortedAtDropOff: false,
      items: [
        {
          id: 11,
          donationId: 6,
          referenceNumber: 'ITEM-011',
          scope: DonationScope.Items,
          categoryKey: 'toys',
          quantity: 8,
          estimatedValuePerItem: 10,
          totalEstimatedValue: 80
        },
        {
          id: 12,
          donationId: 6,
          referenceNumber: 'ITEM-012',
          scope: DonationScope.Items,
          categoryKey: 'home_decor',
          quantity: 5,
          estimatedValuePerItem: 15,
          totalEstimatedValue: 75
        },
        {
          id: 13,
          donationId: 6,
          referenceNumber: 'ITEM-013',
          scope: DonationScope.Items,
          categoryKey: 'linens',
          quantity: 4,
          estimatedValuePerItem: 7,
          totalEstimatedValue: 28
        }
      ]
    },
    {
      id: 7,
      companyId: 1,
      referenceNumber: 'DON-007',
      donorId: 7,
      isAssociatedLater: false,
      storeId: 3,
      attendantId: 3,
      donationAt: '2026-03-11T09:15:00',
      status: DonationStatus.Completed,
      method: DonationMethod.WalkIn,
      scope: DonationScope.Items,
      totalItems: 9,
      totalEstimatedValue: 195,
      loyaltyPointsEarned: 39,
      presortProcessCompleted: true,
      wasPreSortedAtDropOff: false,
      items: [
        {
          id: 14,
          donationId: 7,
          referenceNumber: 'ITEM-014',
          scope: DonationScope.Items,
          categoryKey: 'sporting',
          quantity: 6,
          estimatedValuePerItem: 25,
          totalEstimatedValue: 150
        },
        {
          id: 15,
          donationId: 7,
          referenceNumber: 'ITEM-015',
          scope: DonationScope.Items,
          categoryKey: 'shoes',
          quantity: 3,
          estimatedValuePerItem: 15,
          totalEstimatedValue: 45
        }
      ]
    },
    {
      id: 8,
      companyId: 1,
      referenceNumber: 'DON-008',
      donorId: 9,
      isAssociatedLater: false,
      storeId: 1,
      attendantId: 1,
      donationAt: '2026-03-10T13:40:00',
      status: DonationStatus.Completed,
      method: DonationMethod.WalkIn,
      scope: DonationScope.Items,
      totalItems: 26,
      totalEstimatedValue: 445,
      loyaltyPointsEarned: 134,
      presortProcessCompleted: true,
      wasPreSortedAtDropOff: false,
      items: [
        {
          id: 16,
          donationId: 8,
          referenceNumber: 'ITEM-016',
          scope: DonationScope.Items,
          categoryKey: 'furniture',
          quantity: 1,
          estimatedValuePerItem: 125,
          totalEstimatedValue: 125
        },
        {
          id: 17,
          donationId: 8,
          referenceNumber: 'ITEM-017',
          scope: DonationScope.Items,
          categoryKey: 'housewares',
          quantity: 15,
          estimatedValuePerItem: 8,
          totalEstimatedValue: 120
        },
        {
          id: 18,
          donationId: 8,
          referenceNumber: 'ITEM-018',
          scope: DonationScope.Items,
          categoryKey: 'accessories',
          quantity: 10,
          estimatedValuePerItem: 20,
          totalEstimatedValue: 200
        }
      ]
    },
    {
      id: 9,
      companyId: 1,
      referenceNumber: 'DON-009',
      donorId: 10,
      isAssociatedLater: false,
      storeId: 2,
      attendantId: 2,
      donationAt: '2026-03-09T16:20:00',
      status: DonationStatus.Completed,
      method: DonationMethod.WalkIn,
      scope: DonationScope.Items,
      totalItems: 28,
      totalEstimatedValue: 196,
      loyaltyPointsEarned: 39,
      presortProcessCompleted: true,
      wasPreSortedAtDropOff: false,
      items: [
        {
          id: 19,
          donationId: 9,
          referenceNumber: 'ITEM-019',
          scope: DonationScope.Items,
          categoryKey: 'books',
          quantity: 20,
          estimatedValuePerItem: 5,
          totalEstimatedValue: 100
        },
        {
          id: 20,
          donationId: 9,
          referenceNumber: 'ITEM-020',
          scope: DonationScope.Items,
          categoryKey: 'seasonal',
          quantity: 8,
          estimatedValuePerItem: 12,
          totalEstimatedValue: 96
        }
      ]
    },
    {
      id: 10,
      companyId: 1,
      referenceNumber: 'DON-010',
      donorId: 11,
      isAssociatedLater: false,
      storeId: 1,
      attendantId: 1,
      donationAt: '2026-03-08T10:00:00',
      status: DonationStatus.Completed,
      method: DonationMethod.WalkIn,
      scope: DonationScope.Items,
      totalItems: 17,
      totalEstimatedValue: 280,
      loyaltyPointsEarned: 56,
      presortProcessCompleted: true,
      wasPreSortedAtDropOff: false,
      items: [
        {
          id: 21,
          donationId: 10,
          referenceNumber: 'ITEM-021',
          scope: DonationScope.Items,
          categoryKey: 'clothing',
          quantity: 15,
          estimatedValuePerItem: 12,
          totalEstimatedValue: 180
        },
        {
          id: 22,
          donationId: 10,
          referenceNumber: 'ITEM-022',
          scope: DonationScope.Items,
          categoryKey: 'electronics',
          quantity: 2,
          estimatedValuePerItem: 50,
          totalEstimatedValue: 100
        }
      ]
    },
    {
      id: 11,
      companyId: 1,
      referenceNumber: 'DON-011',
      donorId: 1,
      isAssociatedLater: false,
      storeId: 1,
      attendantId: 1,
      donationAt: '2026-03-01T14:25:00',
      status: DonationStatus.Completed,
      method: DonationMethod.WalkIn,
      scope: DonationScope.Items,
      totalItems: 24,
      totalEstimatedValue: 158,
      loyaltyPointsEarned: 32,
      presortProcessCompleted: true,
      wasPreSortedAtDropOff: false,
      items: [
        {
          id: 23,
          donationId: 11,
          referenceNumber: 'ITEM-023',
          scope: DonationScope.Items,
          categoryKey: 'books',
          quantity: 8,
          estimatedValuePerItem: 5,
          totalEstimatedValue: 40
        },
        {
          id: 24,
          donationId: 11,
          referenceNumber: 'ITEM-024',
          scope: DonationScope.Items,
          categoryKey: 'housewares',
          quantity: 6,
          estimatedValuePerItem: 8,
          totalEstimatedValue: 48
        },
        {
          id: 25,
          donationId: 11,
          referenceNumber: 'ITEM-025',
          scope: DonationScope.Items,
          categoryKey: 'linens',
          quantity: 10,
          estimatedValuePerItem: 7,
          totalEstimatedValue: 70
        }
      ]
    },
    {
      id: 12,
      companyId: 1,
      referenceNumber: 'DON-012',
      donorId: 2,
      isAssociatedLater: false,
      storeId: 2,
      attendantId: 2,
      donationAt: '2026-02-28T11:10:00',
      status: DonationStatus.Completed,
      method: DonationMethod.WalkIn,
      scope: DonationScope.Items,
      totalItems: 12,
      totalEstimatedValue: 144,
      loyaltyPointsEarned: 29,
      presortProcessCompleted: true,
      wasPreSortedAtDropOff: false,
      items: [
        {
          id: 26,
          donationId: 12,
          referenceNumber: 'ITEM-026',
          scope: DonationScope.Items,
          categoryKey: 'clothing',
          quantity: 12,
          estimatedValuePerItem: 12,
          totalEstimatedValue: 144
        }
      ]
    },
    {
      id: 13,
      companyId: 1,
      referenceNumber: 'DON-013',
      donorId: 14,
      isAssociatedLater: false,
      storeId: 1,
      attendantId: 1,
      donationAt: '2026-02-25T15:30:00',
      status: DonationStatus.Completed,
      method: DonationMethod.WalkIn,
      scope: DonationScope.Items,
      totalItems: 12,
      totalEstimatedValue: 540,
      loyaltyPointsEarned: 162,
      presortProcessCompleted: true,
      wasPreSortedAtDropOff: false,
      items: [
        {
          id: 27,
          donationId: 13,
          referenceNumber: 'ITEM-027',
          scope: DonationScope.Items,
          categoryKey: 'furniture',
          quantity: 2,
          estimatedValuePerItem: 125,
          totalEstimatedValue: 250
        },
        {
          id: 28,
          donationId: 13,
          referenceNumber: 'ITEM-028',
          scope: DonationScope.Items,
          categoryKey: 'electronics',
          quantity: 4,
          estimatedValuePerItem: 50,
          totalEstimatedValue: 200
        },
        {
          id: 29,
          donationId: 13,
          referenceNumber: 'ITEM-029',
          scope: DonationScope.Items,
          categoryKey: 'home_decor',
          quantity: 6,
          estimatedValuePerItem: 15,
          totalEstimatedValue: 90
        }
      ]
    }
  ];
  readonly containers: ApiContainerResponse[] = [
    {
      id: 1,
      companyId: 1,
      referenceNumber: 'C-001',
      barcode: 'BRJ-2026-001234',
      donationId: 1,
      containerType: ContainerType.Gaylord,
      presortMethod: PresortMethod.Batch,
      departmentKey: 'clothes',
      categoryKey: 'clothing',
      destination: ContainerDest.Production,
      status: ContainerStatus.Sorting,
      storeId: 1,
      totalItems: 40,
      totalEstimatedValue: 516,
      isSeasonal: false,
      presortedAt: '2026-03-16T08:30:00'
    },
    {
      id: 2,
      companyId: 1,
      referenceNumber: 'C-002',
      barcode: 'BRJ-2026-001235',
      donationId: 2,
      containerType: ContainerType.CartRack,
      presortMethod: PresortMethod.Batch,
      departmentKey: 'media',
      categoryKey: 'books',
      destination: ContainerDest.Production,
      status: ContainerStatus.InUse,
      storeId: 1,
      totalItems: 63,
      totalEstimatedValue: 405,
      isSeasonal: false,
      presortedAt: '2026-03-16T09:00:00'
    },
    {
      id: 3,
      companyId: 1,
      referenceNumber: 'C-003',
      barcode: 'BRJ-2026-001236',
      donationId: 3,
      containerType: ContainerType.Gaylord,
      presortMethod: PresortMethod.DockSide,
      departmentKey: 'clothes',
      destination: ContainerDest.Warehouse,
      status: ContainerStatus.Available,
      storeId: 1,
      totalItems: 50,
      totalEstimatedValue: 600,
      isSeasonal: false
    },
    {
      id: 4,
      companyId: 1,
      referenceNumber: 'C-004',
      barcode: 'BRJ-2026-001237',
      donationId: 5,
      containerType: ContainerType.Tote,
      presortMethod: PresortMethod.DockSide,
      departmentKey: 'media',
      categoryKey: 'electronics',
      destination: ContainerDest.Ecommerce,
      status: ContainerStatus.Sorting,
      storeId: 2,
      totalItems: 8,
      totalEstimatedValue: 400,
      isSeasonal: false,
      presortedAt: '2026-03-15T17:00:00'
    },
    {
      id: 5,
      companyId: 1,
      referenceNumber: 'C-005',
      barcode: 'BRJ-2026-001238',
      containerType: ContainerType.Pallet,
      departmentKey: 'home',
      categoryKey: 'furniture',
      status: ContainerStatus.Available,
      storeId: 1,
      totalItems: 0,
      totalEstimatedValue: 0,
      isSeasonal: false
    },
    {
      id: 6,
      companyId: 1,
      referenceNumber: 'C-006',
      barcode: 'BRJ-2026-001239',
      containerType: ContainerType.Baler,
      departmentKey: 'salvage',
      categoryKey: 'salvage',
      destination: ContainerDest.Salvage,
      status: ContainerStatus.Available,
      storeId: 1,
      totalItems: 0,
      totalEstimatedValue: 0,
      salvageWeightLbs: 120,
      isSeasonal: false,
      notes: 'Textile bale — scheduled for pickup'
    },
    {
      id: 7,
      companyId: 1,
      referenceNumber: 'C-007',
      barcode: 'BRJ-2026-001240',
      containerType: ContainerType.Gaylord,
      presortMethod: PresortMethod.Batch,
      departmentKey: 'clothes',
      status: ContainerStatus.Available,
      storeId: 1,
      totalItems: 65,
      totalEstimatedValue: 0,
      isSeasonal: false
    },
    {
      id: 8,
      companyId: 1,
      referenceNumber: 'C-008',
      barcode: 'BRJ-2026-001241',
      containerType: ContainerType.Pallet,
      presortMethod: PresortMethod.Batch,
      departmentKey: 'home',
      status: ContainerStatus.Available,
      storeId: 1,
      totalItems: 110,
      totalEstimatedValue: 0,
      isSeasonal: false
    }
  ];

  readonly analytics: AnalyticsSummary = {
    totalDonations: 1847,
    totalValue: 124000,
    activeDonors: 3421,
    avgProcessTimeMin: 4.2,
    newDonorsThisMonth: 89,
    repeatDonorRate: 72,
    monetaryDonationVolume: 18400,
    identificationRate: 68,
    trends: [
      { label: 'Apr', count: 320, value: 38400 },
      { label: 'May', count: 410, value: 49200 },
      { label: 'Jun', count: 380, value: 45600 },
      { label: 'Jul', count: 290, value: 34800 },
      { label: 'Aug', count: 440, value: 52800 },
      { label: 'Sep', count: 520, value: 62400 },
      { label: 'Oct', count: 480, value: 57600 },
      { label: 'Nov', count: 600, value: 72000 },
      { label: 'Dec', count: 550, value: 66000 },
      { label: 'Jan', count: 390, value: 46800 },
      { label: 'Feb', count: 470, value: 56400 },
      { label: 'Mar', count: 624, value: 74880 }
    ],
    categoryBreakdown: [
      { categoryKey: 'clothing', categoryName: 'Clothing', color: '#0066ff', count: 187, percentage: 30 },
      { categoryKey: 'books', categoryName: 'Books', color: '#10b981', count: 125, percentage: 20 },
      { categoryKey: 'electronics', categoryName: 'Electronics', color: '#f59e0b', count: 94, percentage: 15 },
      { categoryKey: 'furniture', categoryName: 'Furniture', color: '#8b5cf6', count: 62, percentage: 10 },
      { categoryKey: 'other', categoryName: 'Other', color: '#ef4444', count: 156, percentage: 25 }
    ],
    topLocations: [
      { locationId: 1, locationName: 'Downtown Store', totalValue: 7420, percentage: 100 },
      { locationId: 2, locationName: 'Northside Store', totalValue: 5890, percentage: 79 },
      { locationId: 3, locationName: 'Westside Store', totalValue: 3240, percentage: 44 },
      { locationId: 4, locationName: 'Eastside Store', totalValue: 1850, percentage: 25 }
    ]
  };

  /** Live presort queue — derived from donations not yet presort-completed */
  get presortQueue(): ApiPresortQueueResponse[] {
    return this.donations
      .filter(d => !d.presortProcessCompleted)
      .map(d => ({
        id: d.id,
        companyId: d.companyId,
        donationId: d.id,
        assignedToUserId: null,
        containerId: null,
        status: PresortStatus.Pending,
        receivedAt: d.donationAt
      }));
  }

  /** Compute today's presort stats from the containers array (simplified — no contents data in API model) */
  getPresortStats(): {
    sortedToday: number;
    itemsProcessed: number;
    avgSortMinutes: number;
    salvageRate: number;
    ecommerceItems: number;
    oldestQueuedMins: number;
    deptVolume: { name: string; pct: number; color: string }[];
  } {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sorted = this.containers.filter(
      c =>
        c.status !== ContainerStatus.Available &&
        c.status !== ContainerStatus.ReadyForSorting &&
        c.presortedAt != null &&
        new Date(c.presortedAt) >= today
    );
    const itemsProcessed = sorted.reduce((s, c) => s + c.totalItems, 0);
    const oldestQueuedMins =
      this.presortQueue.length > 0
        ? Math.max(...this.presortQueue.map(q => Math.floor((Date.now() - new Date(q.receivedAt).getTime()) / 60000)))
        : 0;

    return {
      sortedToday: sorted.length,
      itemsProcessed,
      avgSortMinutes: 12,
      salvageRate: 0,
      ecommerceItems: 0,
      oldestQueuedMins,
      deptVolume: []
    };
  }

  getTier(tier: DonorTier): LoyaltyTierConfigResponse {
    return this.loyaltyTiers.find(t => t.tier === tier) ?? this.loyaltyTiers[0];
  }

  getInitials(first: string, last: string): string {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  }

  newReceipt(): string {
    return `DN-${String(Math.floor(Math.random() * 999999)).padStart(6, '0')}`;
  }

  newBarcode(): string {
    return `BRJ-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 999999)).padStart(6, '0')}`;
  }

  // ─── Helper Methods ───

  getDonorById(id: number): ApiDonorResponse | undefined {
    return this.donors.find(d => d.id === id);
  }

  getDonationsByDonor(donorId: number): DonationResponse[] {
    return this.donations
      .filter(d => d.donorId === donorId)
      .sort((a, b) => new Date(b.donationAt).getTime() - new Date(a.donationAt).getTime());
  }

  getContainersByStore(storeId: number): ApiContainerResponse[] {
    return this.containers.filter(c => c.storeId === storeId);
  }

  getDonationById(id: number): DonationResponse | undefined {
    return this.donations.find(d => d.id === id);
  }

  getLocationById(id: number): Location | undefined {
    return this.locations.find(l => l.id === id);
  }

  getCategoryByKey(key: string): DonationCategory | undefined {
    return this.categories.find(c => c.key === key);
  }

  // Simulated creation methods (would be API calls in production)
  createDonation(donation: Partial<DonationResponse>): DonationResponse {
    const newDonation: DonationResponse = {
      id: Date.now(),
      companyId: 1,
      referenceNumber: `DON-${Date.now()}`,
      donorId: donation.donorId ?? null,
      isAssociatedLater: false,
      storeId: donation.storeId ?? this.session.locationId,
      attendantId: donation.attendantId ?? this.session.staffId,
      donationAt: new Date().toISOString(),
      status: DonationStatus.Completed,
      method: donation.method ?? DonationMethod.WalkIn,
      scope: donation.scope ?? DonationScope.Items,
      items: donation.items ?? [],
      totalItems: donation.totalItems ?? 0,
      totalEstimatedValue: donation.totalEstimatedValue ?? 0,
      loyaltyPointsEarned: donation.loyaltyPointsEarned ?? 0,
      presortProcessCompleted: false,
      wasPreSortedAtDropOff: false
    };
    this.donations.unshift(newDonation);
    this.storage.saveDonations(this.donations);
    return newDonation;
  }

  updateDonation(id: number, updates: Partial<DonationResponse>): DonationResponse | undefined {
    const index = this.donations.findIndex(d => d.id === id);
    if (index >= 0) {
      this.donations[index] = { ...this.donations[index], ...updates };
      this.storage.saveDonations(this.donations);
      return this.donations[index];
    }
    return undefined;
  }

  createContainer(container: Partial<ApiContainerResponse>): ApiContainerResponse {
    const now = new Date().toISOString();
    const newContainer: ApiContainerResponse = {
      id: Date.now(),
      companyId: 1,
      referenceNumber: `C-${Date.now()}`,
      barcode: container.barcode ?? this.newBarcode(),
      donationId: container.donationId ?? null,
      containerType: container.containerType ?? ContainerType.Gaylord,
      presortMethod: container.presortMethod ?? null,
      presortWorkerId: null,
      departmentKey: container.departmentKey ?? null,
      categoryKey: container.categoryKey ?? null,
      destination: container.destination ?? null,
      status: container.status ?? ContainerStatus.Available,
      storeId: container.storeId ?? this.session.locationId,
      totalItems: container.totalItems ?? 0,
      totalEstimatedValue: 0,
      salvageWeightLbs: container.salvageWeightLbs ?? null,
      isSeasonal: container.isSeasonal ?? false,
      seasonalTag: container.seasonalTag ?? null,
      parentContainerId: container.parentContainerId ?? null,
      transferToStoreId: null,
      notes: container.notes ?? null,
      presortedAt: container.status !== ContainerStatus.Available ? now : null,
      closedAt: container.closedAt ?? null
    };
    this.containers.unshift(newContainer);
    this.storage.saveContainers(this.containers);
    return newContainer;
  }

  updateContainer(id: number, updates: Partial<ApiContainerResponse>): ApiContainerResponse | undefined {
    const index = this.containers.findIndex(c => c.id === id);
    if (index >= 0) {
      this.containers[index] = { ...this.containers[index], ...updates };
      this.storage.saveContainers(this.containers);
      return this.containers[index];
    }
    return undefined;
  }

  updateDonor(id: number, patch: Partial<ApiDonorResponse>): void {
    const index = this.donors.findIndex(d => d.id === id);
    if (index >= 0) {
      (this.donors as ApiDonorResponse[])[index] = { ...this.donors[index], ...patch };
    }
  }

  addDonor(donor: Partial<ApiDonorResponse>): ApiDonorResponse {
    const newDonor: ApiDonorResponse = {
      id: Date.now(),
      companyId: 1,
      referenceNumber: `D-${Date.now()}`,
      userId: null,
      loyaltyTier: DonorTier.Bronze,
      loyaltyPoints: 0,
      totalDonations: 0,
      lifetimeValue: 0,
      preferredStoreId: donor.preferredStoreId ?? this.session.locationId,
      joinDate: new Date().toISOString().split('T')[0],
      lastDonationDate: null,
      status: ApiStatus.Active,
      ...donor
    };
    (this.donors as ApiDonorResponse[]).unshift(newDonor);
    return newDonor;
  }

  // Simulated rewards/points
  getAvailableRewards() {
    return [
      { id: 1, referenceNumber: 'R-001', name: '$5 Gift Card', pointsRequired: 500, icon: '💳', category: 'gift-card' },
      {
        id: 2,
        referenceNumber: 'R-002',
        name: '$10 Gift Card',
        pointsRequired: 1000,
        icon: '💳',
        category: 'gift-card'
      },
      {
        id: 3,
        referenceNumber: 'R-003',
        name: '$25 Gift Card',
        pointsRequired: 2500,
        icon: '💳',
        category: 'gift-card'
      },
      {
        id: 4,
        referenceNumber: 'R-004',
        name: 'Free Pickup Service',
        pointsRequired: 300,
        icon: '🚚',
        category: 'service'
      },
      {
        id: 5,
        referenceNumber: 'R-005',
        name: 'Priority Processing',
        pointsRequired: 150,
        icon: '⚡',
        category: 'service'
      },
      {
        id: 6,
        referenceNumber: 'R-006',
        name: 'Thank You Mug',
        pointsRequired: 800,
        icon: '☕',
        category: 'merchandise'
      },
      { id: 7, referenceNumber: 'R-007', name: 'Tote Bag', pointsRequired: 600, icon: '👜', category: 'merchandise' },
      {
        id: 8,
        referenceNumber: 'R-008',
        name: 'Branded T-Shirt',
        pointsRequired: 1200,
        icon: '👕',
        category: 'merchandise'
      }
    ];
  }

  getScheduledPickups(donorId?: number) {
    return this.getScheduledDonations(donorId);
  }

  getScheduledDonations(donorId?: number): import('../models/domain.models').ScheduledDonation[] {
    const appts: import('../models/domain.models').ScheduledDonation[] = [
      {
        id: 1,
        referenceNumber: 'APT-20260320-001',
        donorId: 1,
        donorName: 'Michael Johnson',
        donorPhone: '(555) 101-2020',
        donorEmail: 'michael.j@email.com',
        address: '123 Main St, Springfield',
        locationId: 1,
        locationName: 'Downtown Store',
        method: DonationMethod.Scheduled,
        date: new Date('2026-03-20'),
        timeSlot: '9:00 AM',
        status: DonationStatus.Scheduled,
        itemCount: 12,
        categories: ['Furniture', 'Home Décor'],
        recurring: 'One-time',
        notes: 'Large furniture items — needs loading dock',
        createdAt: new Date('2026-03-12')
      },
      {
        id: 2,
        referenceNumber: 'APT-20260322-002',
        donorId: 2,
        donorName: 'Sarah Williams',
        donorPhone: '(555) 234-5678',
        donorEmail: 'sarah.w@email.com',
        address: '456 Oak Ave, Springfield',
        locationId: 1,
        locationName: 'Downtown Store',
        method: DonationMethod.Scheduled,
        date: new Date('2026-03-22'),
        timeSlot: '1:00 PM',
        status: DonationStatus.Scheduled,
        itemCount: 8,
        categories: ['Clothing', 'Shoes'],
        recurring: 'Monthly',
        notes: 'Clothing boxes — approx 8 boxes',
        createdAt: new Date('2026-03-14')
      },
      {
        id: 3,
        referenceNumber: 'APT-20260318-003',
        donorId: 5,
        donorName: 'David Chen',
        donorPhone: '(555) 555-9900',
        donorEmail: 'david.c@email.com',
        address: '555 Maple Dr, Springfield',
        locationId: 2,
        locationName: 'Westside Thrift',
        method: DonationMethod.Scheduled,
        date: new Date('2026-03-18'),
        timeSlot: '9:00 AM',
        status: DonationStatus.Completed,
        itemCount: 5,
        categories: ['Electronics', 'Books'],
        recurring: 'One-time',
        notes: 'Electronics and books',
        createdAt: new Date('2026-03-10')
      },
      {
        id: 4,
        referenceNumber: 'APT-20260325-004',
        donorId: 6,
        donorName: 'Jennifer Taylor',
        donorPhone: '(555) 321-6543',
        donorEmail: 'jen.t@email.com',
        address: '111 Cedar Ln, Springfield',
        locationId: 1,
        locationName: 'Downtown Store',
        method: DonationMethod.Scheduled,
        date: new Date('2026-03-25'),
        timeSlot: '2:00 PM',
        status: DonationStatus.Scheduled,
        itemCount: 20,
        categories: ['Books', 'Home Décor', 'Toys & Games'],
        recurring: 'One-time',
        notes: 'Books and home decor',
        createdAt: new Date('2026-03-15')
      },
      {
        id: 5,
        referenceNumber: 'APT-20260319-005',
        donorId: 9,
        donorName: 'Thomas Brown',
        donorPhone: '(555) 777-1234',
        donorEmail: 'thomas.b@email.com',
        address: '444 Willow Ct, Springfield',
        locationId: 3,
        locationName: 'Eastside Thrift',
        method: DonationMethod.Scheduled,
        date: new Date('2026-03-19'),
        timeSlot: '10:00 AM',
        status: DonationStatus.Cancelled,
        itemCount: 3,
        categories: ['Clothing'],
        recurring: 'One-time',
        notes: 'Cancelled by donor',
        createdAt: new Date('2026-03-11')
      },
      {
        id: 6,
        referenceNumber: 'APT-20260328-006',
        donorName: 'Anonymous',
        locationId: 2,
        locationName: 'Westside Thrift',
        method: DonationMethod.WalkIn,
        date: new Date('2026-03-28'),
        timeSlot: '11:00 AM',
        status: DonationStatus.Scheduled,
        itemCount: 6,
        categories: ['Clothing', 'Shoes'],
        recurring: 'One-time',
        notes: 'Walk-in scheduled by phone',
        createdAt: new Date('2026-03-16')
      },
      {
        id: 7,
        referenceNumber: 'APT-20260401-007',
        donorId: 3,
        donorName: 'Robert Martinez',
        donorPhone: '(555) 456-7890',
        donorEmail: 'roberto.m@email.com',
        address: '789 Elm St, Springfield',
        locationId: 1,
        locationName: 'Downtown Store',
        method: DonationMethod.Scheduled,
        date: new Date('2026-04-01'),
        timeSlot: '3:00 PM',
        status: DonationStatus.Scheduled,
        itemCount: 15,
        categories: ['Furniture', 'Clothing', 'Electronics'],
        recurring: 'Every 2 Weeks',
        notes: 'Regular donor — gold tier',
        createdAt: new Date('2026-03-16')
      }
    ];
    return donorId ? appts.filter(a => a.donorId === donorId) : appts;
  }

  createPickup(pickup: any) {
    // Simulate creating a pickup
    return {
      id: Date.now(),
      ...pickup,
      status: DonationStatus.Scheduled,
      createdAt: new Date()
    };
  }

  // ── Phase 1: Loyalty point calculation (Req 4) ────────────────────────────

  calculatePoints(itemCount: number): number {
    return itemCount * this.appConfig().pointsPerItem;
  }

  // ── Phase 1: Donor association (Req 1) ────────────────────────────────────

  /**
   * Returns true if the donation is eligible for donor association:
   * - status is Completed
   * - has no existing donorId or associatedDonorId
   * - within the configurable window
   */
  canAssociateDonor(donation: DonationResponse): boolean {
    if (donation.status !== DonationStatus.Completed) return false;
    if (donation.donorId) return false;
    const windowMs = this.appConfig().associationWindowHours * 60 * 60 * 1000;
    return Date.now() - new Date(donation.donationAt).getTime() <= windowMs;
  }

  /**
   * Associates an anonymous donation with a donor.
   * Updates the donation record and recalculates loyalty points.
   * Returns the updated donation, or null if ineligible.
   */
  associateDonorToDonation(donationId: number, donorId: number): DonationResponse | null {
    const donor = this.donors.find(d => d.id === donorId);
    if (!donor) return null;

    const target = this.donations.find(d => d.id === donationId);
    if (!target || !this.canAssociateDonor(target)) return null;

    const recalcPoints = this.calculatePoints(target.totalItems);
    const updated: DonationResponse = {
      ...target,
      donorId,
      isAssociatedLater: true,
      associatedAt: new Date().toISOString(),
      loyaltyPointsEarned: recalcPoints
    };
    this.updateDonation(donationId, updated);
    return updated;
  }

  // ── Phase 1: Reward definitions management (Req 5) ────────────────────────

  addRewardDefinition(
    def: Omit<ApiRewardDefinitionResponse, 'id' | 'referenceNumber' | 'totalRedemptions'>
  ): ApiRewardDefinitionResponse {
    const newDef: ApiRewardDefinitionResponse = {
      ...def,
      id: Date.now(),
      referenceNumber: `RD-${Date.now()}`,
      totalRedemptions: 0
    };
    this._rewardDefs.update(list => [...list, newDef]);
    return newDef;
  }

  updateRewardDefinition(id: number, patch: Partial<ApiRewardDefinitionResponse>): void {
    this._rewardDefs.update(list => list.map(d => (d.id === id ? { ...d, ...patch } : d)));
  }

  removeRewardDefinition(id: number): void {
    this._rewardDefs.update(list => list.filter(d => d.id !== id));
  }

  // ── Phase 1: Reward redemption (Req 5) ────────────────────────────────────

  /**
   * Validates and redeems a reward for a donor.
   * Checks: active, validity window, points, per-user limit, global limit.
   * Deducts points atomically and creates a transaction.
   */
  redeemReward(donorId: number, rewardId: number): ApiRewardTransactionResponse | null {
    const donor = this.donors.find(d => d.id === donorId);
    const def = this._rewardDefs().find(d => d.id === rewardId);
    if (!donor || !def || def.status !== ApiStatus.Active) return null;

    // Validity window check
    const now = new Date();
    if (def.validFrom && now < new Date(def.validFrom)) return null;
    if (def.validTo && now > new Date(def.validTo)) return null;

    // Points check
    if (donor.loyaltyPoints < def.pointsRequired) return null;

    // Per-user limit
    if (def.maxRedemptionsPerUser != null) {
      const userCount = this._rewardTxns().filter(
        t =>
          t.donorId === donorId &&
          t.rewardId === rewardId &&
          t.status !== RedemptionStatus.Cancelled &&
          t.status !== RedemptionStatus.Rejected
      ).length;
      if (userCount >= def.maxRedemptionsPerUser) return null;
    }

    // Global limit
    if (def.totalRedemptionLimit != null && def.totalRedemptions >= def.totalRedemptionLimit) return null;

    // Deduct points
    (donor as any).loyaltyPoints -= def.pointsRequired;

    // Increment totalRedemptions
    this._rewardDefs.update(list =>
      list.map(d => (d.id === rewardId ? { ...d, totalRedemptions: d.totalRedemptions + 1 } : d))
    );

    const autoApprove = !this.appConfig().requireApproval;
    const txn: ApiRewardTransactionResponse = {
      id: Date.now(),
      companyId: 1,
      referenceNumber: `RTX-${Date.now()}`,
      donorId,
      rewardId,
      pointsUsed: def.pointsRequired,
      status: autoApprove ? RedemptionStatus.Approved : RedemptionStatus.Pending,
      isGift: false,
      approvedAt: autoApprove ? now.toISOString() : undefined,
      voucherCode: def.rewardType === RewardType.Voucher ? `VCH-${Date.now().toString(36).toUpperCase()}` : undefined
    };
    this._rewardTxns.update(list => [txn, ...list]);
    return txn;
  }

  /**
   * Gifts a reward from one donor to another.
   * Deducts points from the gifter; creates two transactions — one on each donor's history.
   * Returns the gifter's transaction, or null if ineligible.
   */
  giftReward(fromDonorId: number, toDonorId: number, rewardId: number): ApiRewardTransactionResponse | null {
    const fromDonor = this.donors.find(d => d.id === fromDonorId);
    const toDonor = this.donors.find(d => d.id === toDonorId);
    const def = this._rewardDefs().find(d => d.id === rewardId);
    if (!fromDonor || !toDonor || !def || def.status !== ApiStatus.Active || !def.isGiftable) return null;
    if (fromDonorId === toDonorId) return null;

    const now = new Date();
    if (def.validFrom && now < new Date(def.validFrom)) return null;
    if (def.validTo && now > new Date(def.validTo)) return null;
    if (fromDonor.loyaltyPoints < def.pointsRequired) return null;
    if (def.totalRedemptionLimit != null && def.totalRedemptions >= def.totalRedemptionLimit) return null;

    (fromDonor as any).loyaltyPoints -= def.pointsRequired;
    this._rewardDefs.update(list =>
      list.map(d => (d.id === rewardId ? { ...d, totalRedemptions: d.totalRedemptions + 1 } : d))
    );

    const nowIso = now.toISOString();
    const voucherCode =
      def.rewardType === RewardType.Voucher ? `VCH-${Date.now().toString(36).toUpperCase()}` : undefined;

    const gifterTxn: ApiRewardTransactionResponse = {
      id: Date.now(),
      companyId: 1,
      referenceNumber: `RTX-${Date.now()}`,
      donorId: fromDonorId,
      rewardId,
      pointsUsed: def.pointsRequired,
      status: RedemptionStatus.Fulfilled,
      isGift: true,
      giftedToId: toDonorId,
      approvedAt: nowIso,
      fulfilledAt: nowIso,
      voucherCode
    };
    const recipientTxn: ApiRewardTransactionResponse = {
      id: Date.now() + 1,
      companyId: 1,
      referenceNumber: `RTX-${Date.now() + 1}`,
      donorId: toDonorId,
      rewardId,
      pointsUsed: 0,
      status: RedemptionStatus.Approved,
      isGift: true,
      giftedFromId: fromDonorId,
      approvedAt: nowIso,
      voucherCode
    };
    this._rewardTxns.update(list => [gifterTxn, recipientTxn, ...list]);
    return gifterTxn;
  }

  // ── Phase 1: Redemption status management ─────────────────────────────────

  approveRedemption(txnId: number): boolean {
    let ok = false;
    this._rewardTxns.update(list =>
      list.map(t => {
        if (t.id === txnId && t.status === RedemptionStatus.Pending) {
          ok = true;
          return { ...t, status: RedemptionStatus.Approved, approvedAt: new Date().toISOString() };
        }
        return t;
      })
    );
    return ok;
  }

  rejectRedemption(txnId: number, reason?: string): boolean {
    let ok = false;
    this._rewardTxns.update(list =>
      list.map(t => {
        if (t.id === txnId && t.status === RedemptionStatus.Pending) {
          ok = true;
          // Refund points
          const donor = this.donors.find(d => d.id === t.donorId);
          if (donor) (donor as any).loyaltyPoints += t.pointsUsed;
          return {
            ...t,
            status: RedemptionStatus.Rejected,
            rejectedAt: new Date().toISOString(),
            rejectionReason: reason
          };
        }
        return t;
      })
    );
    return ok;
  }

  fulfillRedemption(txnId: number): boolean {
    let ok = false;
    this._rewardTxns.update(list =>
      list.map(t => {
        if (t.id === txnId && t.status === RedemptionStatus.Approved) {
          ok = true;
          return { ...t, status: RedemptionStatus.Fulfilled, fulfilledAt: new Date().toISOString() };
        }
        return t;
      })
    );
    return ok;
  }

  cancelRedemption(txnId: number): boolean {
    let ok = false;
    this._rewardTxns.update(list =>
      list.map(t => {
        if (t.id === txnId && (t.status === RedemptionStatus.Pending || t.status === RedemptionStatus.Approved)) {
          ok = true;
          const donor = this.donors.find(d => d.id === t.donorId);
          if (donor) (donor as any).loyaltyPoints += t.pointsUsed;
          return { ...t, status: RedemptionStatus.Cancelled, cancelledAt: new Date().toISOString() };
        }
        return t;
      })
    );
    return ok;
  }

  /** Get available rewards for a donor (active, within validity, eligible) */
  getAvailableRewardsForDonor(donorId: number): ApiRewardDefinitionResponse[] {
    const donor = this.donors.find(d => d.id === donorId);
    if (!donor) return [];
    const now = new Date();
    return this._rewardDefs().filter(def => {
      if (def.status !== ApiStatus.Active) return false;
      if (def.validFrom && now < new Date(def.validFrom)) return false;
      if (def.validTo && now > new Date(def.validTo)) return false;
      if (def.totalRedemptionLimit != null && def.totalRedemptions >= def.totalRedemptionLimit) return false;
      if (def.maxRedemptionsPerUser != null) {
        const userCount = this._rewardTxns().filter(
          t =>
            t.donorId === donorId &&
            t.rewardId === def.id &&
            t.status !== RedemptionStatus.Cancelled &&
            t.status !== RedemptionStatus.Rejected
        ).length;
        if (userCount >= def.maxRedemptionsPerUser) return false;
      }
      return true;
    });
  }

  getRewardTransactionsForDonor(donorId: number): ApiRewardTransactionResponse[] {
    return this._rewardTxns().filter(t => t.donorId === donorId);
  }

  // ── Phase 1: Campaign management (Req 6) ──────────────────────────────────

  createCampaign(campaign: Omit<Campaign, 'id' | 'referenceNumber' | 'notificationHistory' | 'createdAt'>): Campaign {
    const newCampaign: Campaign = {
      ...campaign,
      id: Date.now(),
      referenceNumber: `CMP-${Date.now()}`,
      notificationHistory: [],
      createdAt: new Date()
    };
    this._campaigns.update(list => [newCampaign, ...list]);
    return newCampaign;
  }

  updateCampaign(id: number, patch: Partial<Campaign>): void {
    this._campaigns.update(list => list.map(c => (c.id === id ? { ...c, ...patch } : c)));
  }

  saveCampaignTemplate(campaignId: number, template: NotificationTemplate): void {
    const field = template.channel === NotificationChannel.Email ? 'emailTemplate' : 'smsTemplate';
    this._campaigns.update(list =>
      list.map(c => (c.id === campaignId ? { ...c, [field]: { ...template, updatedAt: new Date() } } : c))
    );
  }

  /** Resolve merge variables in a template body/subject using a sample donor */
  previewTemplate(text: string, donor: ApiDonorResponse, campaignName: string): string {
    const fullName = donor.firstName ? `${donor.firstName} ${donor.lastName ?? ''}`.trim() : donor.referenceNumber;
    const firstName = donor.firstName ?? donor.referenceNumber;
    return text
      .replace(/\{\{donor_name\}\}/g, fullName)
      .replace(/\{\{first_name\}\}/g, firstName)
      .replace(/\{\{campaign_name\}\}/g, campaignName)
      .replace(/\{\{points\}\}/g, donor.loyaltyPoints.toLocaleString())
      .replace(/\{\{tier\}\}/g, DonorTierLabel[donor.loyaltyTier])
      .replace(/\{\{org_name\}\}/g, 'Our Organization');
  }

  /**
   * Executes a campaign: finds eligible donors by targetCriteria,
   * simulates sending notifications, and appends to notificationHistory.
   */
  executeCampaign(campaignId: number): CampaignNotification[] {
    const campaign = this._campaigns().find(c => c.id === campaignId);
    if (!campaign || campaign.status !== CampaignStatus.Active) return [];

    // Find donors who have donated items matching any criterion
    const eligibleDonors = this.donors.filter(() => {
      // emailForCampaigns: donor email is not stored in ApiDonorResponse; allow all in mock
      return true;
    });

    const now = new Date();
    const newNotifications: CampaignNotification[] = eligibleDonors.map(donor => ({
      donorId: donor.id,
      donorName: donor.firstName ? `${donor.firstName} ${donor.lastName ?? ''}`.trim() : donor.referenceNumber,
      channel: campaign.channel,
      sentAt: now,
      success: Math.random() > 0.05
    }));

    this._campaigns.update(list =>
      list.map(c =>
        c.id === campaignId
          ? {
              ...c,
              status: CampaignStatus.Active,
              notificationHistory: [...c.notificationHistory, ...newNotifications]
            }
          : c
      )
    );
    return newNotifications;
  }
}
